import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FIELD_TYPE_OPTIONS } from '../../constants';

interface InputSectionProps {
  details: string;
  onDetailsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPaste: (event: React.ClipboardEvent) => void;
  isSubmitting: boolean;
  selectValue: string;
  onSelectChange: (value: string) => void;
  onAddFile: () => void;
  onSubmit: () => void;
  isForwardEnabled: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const InputSection: React.FC<InputSectionProps> = ({
  details,
  onDetailsChange,
  onPaste,
  isSubmitting,
  selectValue,
  onSelectChange,
  onAddFile,
  onSubmit,
  isForwardEnabled,
  fileInputRef,
  onFileUpload,
}) => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const fieldTypeOptions = FIELD_TYPE_OPTIONS;

  const groups = Array.from(new Set(fieldTypeOptions.map((o) => o.group)));

  const selectedItemSx = {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '100%',
    letterSpacing: '-0.02em',
    width: '100%',
    minWidth: '100%',
    boxSizing: 'border-box',
    py: 1.5,
    px: 1.25,
    mx: 0.4,
    justifyContent: 'flex-start',
    textAlign: 'left',
    '&.Mui-selected': {
      backgroundColor: '#ECF2FF !important',
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#ECF2FF !important',
    },
  };

  const subheaderSx = {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '14px',
    color: '#737373',
    lineHeight: '24px',
    letterSpacing: '0.05em',
    borderTop: '1px solid #E5E7EB',
    px: 1,
    py: 0.5,
  };

  const renderGroupedOptions = () => {
    const items: React.ReactNode[] = [];
    groups.forEach((group) => {
      items.push(
        <ListSubheader key={`group-${group}`} sx={subheaderSx}>
          {group}
        </ListSubheader>
      );
      fieldTypeOptions
        .filter((o) => o.group === group)
        .forEach((option) => {
          items.push(
            <MenuItem key={option.value} value={option.value} sx={selectedItemSx}>
              {option.label}
              {option.value === selectValue && (
                <img src="/images/blueTick.svg" alt="selected" style={{ marginLeft: 'auto' }} />
              )}
            </MenuItem>
          );
        });
    });
    return items;
  };

  const buttonHeight = selectValue ? '52px' : '40px';

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          width: '100%',
          mb: 1.5,
        }}
      >
        <TextField
          fullWidth
          multiline
          placeholder="Add instructions to generate knowledge"
          value={details}
          onChange={onDetailsChange}
          minRows={1}
          maxRows={5}
          onPaste={onPaste}
          variant="standard"
          disabled={isSubmitting}
          sx={{
            '& .MuiInputBase-input::placeholder': {
              color: '#737373',
              opacity: 1,
            },
          }}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: '16px',
              color: '#000000',
              '& textarea': {
                minHeight: '24px',
                overflowY: 'auto !important',
              },
            },
          }}
        />
      </Box>

      {/* Bottom section with field type selector and add file button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          mt: 0.5,
          gap: 1,
          flexWrap: isMobile ? 'nowrap' : 'nowrap', // Keep it on one line but ensure children can shrink
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <FormControl size="small" sx={{ flexShrink: 1, minWidth: 0 }}>
            <Select
              value={selectValue}
              onChange={(e) => onSelectChange(e.target.value as string)}
              displayEmpty
              disabled={isSubmitting}
              IconComponent={KeyboardArrowDownIcon}
              renderValue={(value) => {
                const selectedFieldValue = value as string;
                if (!selectedFieldValue) {
                  return (
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 400,
                        fontSize: isMobile ? '12px' : '14px',
                        color: 'rgba(0, 0, 0, 0.38)',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {isMobile ? 'Category' : 'Select Knowledge Category'}
                    </Typography>
                  );
                }
                const matchedOption = fieldTypeOptions.find((o) => o.value === selectedFieldValue);
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '12px',
                        color: '#737373',
                        textAlign: 'left',
                        lineHeight: 1,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {matchedOption?.group}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#131426',
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isMobile ? '100px' : undefined,
                      }}
                    >
                      {matchedOption?.label}
                    </Typography>
                  </Box>
                );
              }}
              inputProps={{ 'aria-label': 'field type' }}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },

                PaperProps: {
                  sx: {
                    width: 'max-content',
                    boxShadow: 'none',
                    border: '1px solid #B9B9B9',
                    borderRadius: '6px',
                    color: '#131426',
                    mt: 1,
                    overflow: 'hidden',
                  },
                },
                MenuListProps: {
                  sx: {
                    maxHeight: 'min(320px, 70vh)',
                    width: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingTop: 0,
                    paddingBottom: 0,
                    color: '#131426',

                    '&::-webkit-scrollbar': {
                      width: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#c7c7c7',
                      borderRadius: '4px',
                    },
                  },
                },
              }}
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: '16px',
                lineHeight: '100%',
                letterSpacing: '-0.02em',
                width: 'auto',
                // height: selectValue ? '48px' : '40px',
                height: buttonHeight,
                opacity: 1,
                borderRadius: '6px',
                color: '#131426',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(185, 185, 185, 1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d0d0d0',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#898989',
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: selectValue ? 'flex-start' : 'center',
                  gap: '11px',
                  color: '#131426',
                  paddingTop: selectValue ? '10px' : undefined,
                  paddingBottom: selectValue ? '10px' : undefined,
                },
                '& .MuiSelect-icon': {
                  color: '#131426',
                  transition: 'transform 150ms ease',
                },
                '& .MuiSelect-iconOpen': {
                  transform: 'rotate(180deg)',
                },
              }}
            >
              {renderGroupedOptions()}
            </Select>
          </FormControl>

          <Button
            startIcon={<AttachFileIcon sx={{ fontSize: 18, transform: 'rotate(45deg)' }} />}
            variant="outlined"
            size="small"
            onClick={onAddFile}
            disabled={isSubmitting}
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '100%',
              letterSpacing: '-0.02em',
              color: '#131426',
              borderColor: 'rgba(185, 185, 185, 1)',
              textTransform: 'none',
              borderRadius: '6px',
              // width: '105.34px',
              // height: selectValue ? '48px' : '40px',
              width: isMobile ? '80px' : '105.34px',
              height: buttonHeight,
              flexShrink: 0,
              opacity: 1,
              gap: '11px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#d0d0d0' },
              '& .MuiButton-startIcon': { margin: 0 },
            }}
          >
            {isMobile ? 'File' : 'Add file'}
          </Button>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={onSubmit}
          disabled={!isForwardEnabled || isSubmitting}
          sx={{
            backgroundColor: '#2563eb',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '13px',
            borderRadius: '6px',
            minWidth: 'auto',
            width: buttonHeight,
            height: buttonHeight,
            flexShrink: 0, // ← key fix: never shrink
            padding: 0,
            '&:hover': { backgroundColor: '#1d4ed8' },
            '&.Mui-disabled': { backgroundColor: 'rgba(163, 187, 253, 1)', color: 'white' },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={18} sx={{ color: '#999' }} />
          ) : (
            <img src="/images/send-icon.png" alt="send" width={18} height={18} />
          )}
        </Button>
      </Box>

      <input ref={fileInputRef} type="file" multiple hidden onChange={onFileUpload} />
    </>
  );
};

export default InputSection;
